import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { bookingActions } from "../../../../../store/reducers/booking";

import { Grid, Typography, MenuItem } from "@mui/material";

import { DateTime } from "luxon";

import constants from "../../../../../utils/constants";
import LitcoTextField from "../../../../UI/LitcoTextField";
import LitcoRadioField from "../../../../UI/LitcoRadioField";
import LitcoCheckbox from "../../../../UI/LitcoCheckbox";
import DatePicker from "../../../../UI/DatePicker";

const generateNumberOfOccupantsOptions = (maxNumber) => {
  const numberOfOccupantsOptions = [];

  for (let i = 1; i <= maxNumber; i++) {
    numberOfOccupantsOptions.push(
      <MenuItem value={i} key={"occupants" + i}>
        {i}
      </MenuItem>
    );
  }

  return numberOfOccupantsOptions;
};

export default function ConsultClientDetails(props) {
  const { clientDetails, onClientDetailsChange, onClientDetailsChangeCustom, onConfirmDetails, actions } = props;

  const preQualified = useSelector((state) => state.booking.bookingStatus) === constants.BOOKING_STATUS.PRE_QUALIFIED;

  const dispatch = useDispatch();

  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const additionalInfo = clientDetails.additionalInfo;
  const driverOrPassenger = additionalInfo.driverOrPassenger;
  const dateTimeAccidentDate = additionalInfo.accidentDate ? DateTime.fromISO(additionalInfo.accidentDate) : "";
  const numberOfOccupants = additionalInfo.numberOfOccupants;

  const clientAdditionalInfoChangeHandler = (event) => {
    const name = event.target.name;
    let value;

    if (name === "willOthersAttend") {
      value = event.target.checked;
    } else {
      value = event.target.value;
    }

    onClientDetailsChangeCustom((prevState) => ({
      ...prevState,
      additionalInfo: { ...prevState.additionalInfo, [name]: value },
    }));
  };

  const addressChangeHandler = (event) => {
    onClientDetailsChange(event);

    const { street, city, province, postalCode } = clientDetails;
    const address = `${street}, ${city}, ${province} ${postalCode}`;

    onClientDetailsChangeCustom((prevState) => ({
      ...prevState,
      additionalInfo: { ...prevState.additionalInfo, address },
    }));
  };

  const accidentDateChangeHandler = (date) => {
    onClientDetailsChangeCustom((prevState) => ({
      ...prevState,
      additionalInfo: { ...prevState.additionalInfo, accidentDate: date.toISO() },
    }));
  };

  const handleDatePickerOpened = () => {
    //Need this to work with TAB key. Otherwise there is no way to close the dialog since the input is on focus while the dialog is open
    document.getElementsByName("accidentDate")[0].blur();

    setDatePickerOpen(true);
  };

  const handleDatePickerClosed = () => {
    setDatePickerOpen(false);
  };

  const bookingRequestCallback = (data) => {
    const result = data.result;

    if (result === "success") {
      clientDetails.meetingId = data.meetingId;

      if (numberOfOccupants > 1 && additionalInfo.willOthersAttend === true) {
        let otherOccupants = {};

        for (let i = 1; i <= numberOfOccupants - 1; i++) {
          otherOccupants[`Additional Occupant ${i}`] = {
            firstName: "",
            middleName: "",
            lastName: "",
            sameAddress: "yes",
            address: additionalInfo.address,
            isMinor: "no",
            dateOfBirth: "",
            driverOrPassenger: "passenger",
            driverPassengerOther: "",
            attendingConsult: "yes",
          };
        }

        additionalInfo.otherOccupants = otherOccupants;

        dispatch(bookingActions.updateClientDetails(clientDetails));
        if (preQualified) {
          dispatch(bookingActions.updateStatus(constants.BOOKING_STATUS.EXTRA_OCCUPANTS_QUALIFIED));
        } else {
          dispatch(bookingActions.updateStatus(constants.BOOKING_STATUS.EXTRA_OCCUPANTS));
        }
      } else {
        dispatch(bookingActions.updateStatus(constants.BOOKING_STATUS.COMPLETE));
      }
    }
  };

  return (
    <>
      <form onSubmit={(event) => onConfirmDetails(event, bookingRequestCallback)} method="POST" id="clientDetailsForm">
        <Grid container rowSpacing={2} direction={{ xs: "column", sm: "row" }}>
          <Grid item xs={12}>
            <Grid container alignItems="end" spacing={1} sx={{ border: "1px solid #e6e6e6" }} pb={1} pr={1}>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Legal Name(s)</Typography>
              </Grid>
              <LitcoTextField
                xs
                label="First"
                name="firstName"
                required
                value={clientDetails.firstName}
                onChange={onClientDetailsChange}
                maxLength={32}
              />
              <LitcoTextField
                xs
                label="Middle"
                name="middleName"
                value={clientDetails.middleName}
                onChange={onClientDetailsChange}
                maxLength={32}
              />
              <LitcoTextField
                xs
                label="Last"
                name="lastName"
                required
                value={clientDetails.lastName}
                onChange={onClientDetailsChange}
                maxLength={32}
              />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container alignItems="center" spacing={1} sx={{ border: "1px solid #e6e6e6" }} pb={1} pr={1}>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Contact</Typography>
              </Grid>
              <LitcoTextField
                xs
                label="Phone"
                helperText="10 digits, numbers only"
                required
                value={clientDetails.phone}
                onChange={onClientDetailsChange}
                maxLength={11}
                type="numeric"
                pattern="^1?[1-9][0-9]{9}$"
              />
              <LitcoTextField
                xs
                label="Email"
                helperText="email@example.com"
                value={clientDetails.email}
                onChange={onClientDetailsChange}
                maxLength={64}
                type="email"
                pattern="^[a-z0-9._\-]+@[a-z]+\.[a-z]{2,3}$"
              />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container alignItems="start" spacing={1} sx={{ border: "1px solid #e6e6e6" }} pb={1} pr={1}>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Current Mailing Address</Typography>
              </Grid>
              <LitcoTextField
                xs={6}
                sm
                label="Street and Number"
                name="street"
                value={clientDetails.street}
                onChange={addressChangeHandler}
                maxLength={100}
              />
              <LitcoTextField
                xs={6}
                sm={3}
                label="City"
                required
                value={clientDetails.city}
                onChange={addressChangeHandler}
              />
              <LitcoTextField
                xs={6}
                sm={3}
                label="Province"
                value={clientDetails.province}
                onChange={addressChangeHandler}
              />
              <LitcoTextField
                xs={6}
                sm={2}
                label="Postal Code"
                helperText="CA or US format"
                value={clientDetails.postalCode}
                onChange={addressChangeHandler}
                maxLength={20}
                pattern="^(?:[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJKLMNPRSTVWXYZ])[\s\-]?\d[ABCEGHJKLMNPRSTVWXYZ]\d$|^\d{5}(?:-\d{4})?$" //US Zip or CA Postal
              />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container alignItems="end" spacing={1} sx={{ border: "1px solid #e6e6e6" }} pb={1} pr={1}>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Accident Information</Typography>
              </Grid>
              <LitcoTextField
                sm={6}
                label="Approximate Accident Date"
                name="accidentDate"
                value={dateTimeAccidentDate ? dateTimeAccidentDate.toLocaleString(DateTime.DATE_MED) : ""}
                required={!preQualified}
                onFocus={handleDatePickerOpened}
              />
              <LitcoTextField
                sm={6}
                label="Accident City"
                value={clientDetails.accidentCity}
                onChange={onClientDetailsChange}
                maxLength={100}
              />
              <LitcoTextField
                sm={6}
                label="Police File Number"
                value={additionalInfo.fileNumber}
                onChange={clientAdditionalInfoChangeHandler}
              />

              <Grid item sm={6}>
                <Grid container>
                  <Grid item>
                    <LitcoRadioField
                      label="Were you the driver or passenger?"
                      name="driverOrPassenger"
                      value={driverOrPassenger}
                      options={[{ label: "Driver" }, { label: "Passenger" }, { label: "Other" }]}
                      onChange={clientAdditionalInfoChangeHandler}
                      required
                      radioSize="small"
                    />
                  </Grid>

                  {driverOrPassenger && driverOrPassenger === "other" && (
                    <LitcoTextField
                      xs={12}
                      sm
                      label="Please Specify"
                      name="driverPassengerOther"
                      required={driverOrPassenger === "other"}
                      value={additionalInfo.driverOrPassengerOther}
                      onChange={clientAdditionalInfoChangeHandler}
                    />
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" display="block" fontSize="1rem" color="#888888">
                  How many occupants in your vehicle were injured? *
                </Typography>
                <Grid container alignItems="center" justifyContent="space-between">
                  <Grid item xs={numberOfOccupants < 2 ? 12 : undefined}>
                    <LitcoTextField
                      name="numberOfOccupants"
                      select={generateNumberOfOccupantsOptions(8)}
                      required
                      value={numberOfOccupants}
                      onChange={clientAdditionalInfoChangeHandler}
                      fullWidth={numberOfOccupants < 2}
                      width={numberOfOccupants > 1 ? 45 : undefined}
                    />
                  </Grid>

                  {numberOfOccupants > 1 && (
                    <Grid item>
                      <LitcoCheckbox
                        name="willOthersAttend"
                        label="All occupants will attend the consult"
                        checked={additionalInfo.willOthersAttend}
                        onChange={clientAdditionalInfoChangeHandler}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <LitcoTextField
                xs={12}
                sm={6}
                label="Brief description of the accident and your injuries"
                name="description"
                multiline
                rows={4}
                variant="outlined"
                required={!preQualified}
                value={additionalInfo.description}
                onChange={clientAdditionalInfoChangeHandler}
              />
            </Grid>
          </Grid>

          <Grid item xs={12} mt={1}>
            {actions}
          </Grid>
        </Grid>
      </form>

      {datePickerOpen && (
        <DatePicker
          title="Accident Date"
          open={datePickerOpen}
          onClose={handleDatePickerClosed}
          onDateSelected={accidentDateChangeHandler}
          selectedDate={additionalInfo.accidentDate}
          yearsBack={2}
          tooltipText="Must be within two years"
        />
      )}
    </>
  );
}
